// A5 Portrait — PAWN TICKET (no tear-off). Boxed gold-accent layout, one full page.
import React from 'react';
import { resolvePledgePhotoUrl } from '../../../utils/pledgePhotoUrl';

export const PLEDGE_A5_TEMPLATE_NO_TEAR = 'A5_PLEDGE_TICKET_NO_TEAR';

const GOLD = '#c4a04a';
const GOLD_DARK = '#8a6d2f';
const GOLD_SOFT = '#f7f0e0';
const GOLD_LINE = '#e6d5a8';
const INK = '#1a1a1a';

const Icon = ({ children, size = 12 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={GOLD}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0, display: 'block' }}
  >
    {children}
  </svg>
);

const TicketIcon = ({ size = 14 }) => (
  <Icon size={size}>
    <path d="M3 9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4V9z" />
    <path d="M9 5v14" />
  </Icon>
);

const IdIcon = () => (
  <Icon>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <circle cx="9" cy="11" r="2" />
    <path d="M15 10h4M15 14h4" />
  </Icon>
);

const DocIcon = () => (
  <Icon>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6M8 13h8M8 17h5" />
  </Icon>
);

const CalIcon = () => (
  <Icon>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </Icon>
);

const UserIcon = () => (
  <Icon size={11} >
    <circle cx="12" cy="8" r="3.5" />
    <path d="M5 19c1.2-3.2 3.8-5 7-5s5.8 1.8 7 5" />
  </Icon>
);

const RupeeIcon = () => (
  <Icon size={11}>
    <path d="M7 5h10M7 9h10M7 5c4 0 7 2 7 6s-3 6-7 6" />
    <path d="M7 21l6-8" />
  </Icon>
);

const GemIcon = () => (
  <Icon size={11}>
    <path d="M6 9l6-5 6 5-6 11L6 9z" />
    <path d="M6 9h12" />
  </Icon>
);

const CamIcon = () => (
  <Icon size={16}>
    <path d="M4 8h3l2-2h6l2 2h3v11H4z" />
    <circle cx="12" cy="13" r="3.5" />
  </Icon>
);

const PenIcon = () => (
  <Icon size={11}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4 11.5-11.5z" />
  </Icon>
);

const FingerIcon = () => (
  <Icon size={11}>
    <path d="M12 12a3 3 0 0 0-3 3v5" />
    <path d="M12 8a7 7 0 0 0-7 7v1" />
    <path d="M12 4a11 11 0 0 0-4 1.2" />
    <path d="M15 12v8" />
    <path d="M18 13.5V20" />
  </Icon>
);

const ShieldIcon = () => (
  <Icon size={11}>
    <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z" />
    <path d="M9 12l2 2 4-4" />
  </Icon>
);

const SectionTitle = ({ icon, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
    <div
      style={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        background: GOLD_SOFT,
        border: `1px solid ${GOLD_LINE}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {icon}
    </div>
    <div style={{ fontWeight: 800, fontSize: '0.92em', color: GOLD_DARK, letterSpacing: '0.02em' }}>
      {children}
    </div>
  </div>
);

const FieldRow = ({ label, value, highlight, goldValue }) => (
  <div style={{ display: 'flex', gap: 8, marginBottom: 4, alignItems: 'flex-start', fontSize: '0.88em' }}>
    <div style={{ width: 132, flexShrink: 0, color: '#555', fontWeight: 600 }}>{label}</div>
    <div
      style={{
        flex: 1,
        minWidth: 0,
        fontWeight: highlight || goldValue ? 800 : 600,
        color: goldValue ? GOLD_DARK : INK,
        background: highlight ? GOLD_SOFT : 'transparent',
        padding: highlight ? '2px 6px' : 0,
        borderRadius: 4,
        wordBreak: 'break-word',
      }}
    >
      {value || '—'}
    </div>
  </div>
);

const PledgePrintA5NoTear = ({ pledgeData, companyData }) => {
  if (!pledgeData) return null;

  const formatDate = (date) => {
    if (!date) return '—';
    const d = new Date(date);
    return d.toLocaleDateString('en-GB');
  };

  const formatAmount = (amount) => {
    const n = parseFloat(amount);
    if (!Number.isFinite(n)) return '0';
    return n.toLocaleString('en-IN');
  };

  const numberToWords = (num) => {
    if (!num || num === 0) return 'Zero';
    const ones = [
      '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
      'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
      'Seventeen', 'Eighteen', 'Nineteen',
    ];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const numToWords = (n) => {
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + numToWords(n % 100) : '');
      if (n < 100000) return numToWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + numToWords(n % 1000) : '');
      if (n < 10000000) return numToWords(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + numToWords(n % 100000) : '');
      return numToWords(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + numToWords(n % 10000000) : '');
    };
    return 'Rupees ' + numToWords(Math.floor(num)) + ' Only';
  };

  const formatAadhar = (raw) => {
    if (raw == null || raw === '') return '';
    const d = String(raw).replace(/\D/g, '');
    if (d.length === 12) return `${d.slice(0, 4)} ${d.slice(4, 8)} ${d.slice(8, 12)}`;
    return String(raw);
  };

  const resolvePhotoSrc = (stored) => {
    if (stored == null || stored === '') return null;
    if (typeof stored === 'string' && stored.startsWith('blob:')) return stored;
    if (stored instanceof File || stored instanceof Blob) {
      try {
        return URL.createObjectURL(stored);
      } catch {
        return null;
      }
    }
    return resolvePledgePhotoUrl(stored);
  };

  const c = pledgeData.customer || {};
  const pawnerName = `${(c.firstName || '').trim()} ${(c.lastName || '').trim()}`.trim().toUpperCase() || '—';
  const address = [c.address, c.village, c.atPost].filter((x) => String(x || '').trim()).join(', ') || '—';
  const aadhar = formatAadhar(c.aadharCardNo ?? c.aadhar ?? c.aadharNo);
  const pan = String(c.panCardNo ?? c.pan ?? '').trim();
  let idProof = '';
  if (aadhar) idProof = `Aadhar — ${aadhar}`;
  else if (pan) idProof = `PAN — ${pan}`;
  else if (c.voterIdNo || c.voterId) idProof = `Voter ID — ${c.voterIdNo || c.voterId}`;

  const ticketNo = pledgeData.loanDetails?.ticketNo || '—';
  const principal = parseFloat(pledgeData.loanDetails?.principalAmount) || 0;
  const duration = Number(pledgeData.loanDetails?.duration) || 12;
  const roi = pledgeData.loanDetails?.rateOfInterest || '0';
  const articles = pledgeData.articles || [];

  const wt = (a, keys) => {
    for (const k of keys) {
      const n = parseFloat(a?.[k]);
      if (Number.isFinite(n)) return n;
    }
    return 0;
  };

  const rows = articles.map((a, i) => {
    const gross = wt(a, ['grossWt', 'grossWeight']);
    const net = wt(a, ['netWt', 'netWeight']);
    const deduction = Math.max(0, gross - net);
    const qty = parseFloat(a.qty ?? a.quantity) || 1;
    return {
      no: i + 1,
      desc: a.description || a.articleTitle || a.title || a.name || 'Article',
      qty,
      gross,
      deduction,
      net,
      purity: a.purity || '—',
      value: parseFloat(a.valueTotal) || 0,
    };
  });

  const totals = rows.reduce(
    (s, r) => ({
      qty: s.qty + r.qty,
      gross: s.gross + r.gross,
      deduction: s.deduction + r.deduction,
      net: s.net + r.net,
      value: s.value + r.value,
    }),
    { qty: 0, gross: 0, deduction: 0, net: 0, value: 0 }
  );

  const clientPhotoSrc = resolvePhotoSrc(pledgeData.clientPhoto);
  const articlePhotoSrc = resolvePhotoSrc(pledgeData.articlePhoto);
  const thumbImageSrc = pledgeData.fingerprint?.image
    ? pledgeData.fingerprint.image.startsWith('data:')
      ? pledgeData.fingerprint.image
      : `data:image/png;base64,${pledgeData.fingerprint.image}`
    : null;

  const box = {
    border: `1px solid ${GOLD_LINE}`,
    borderRadius: 8,
    padding: '7px 9px',
    marginBottom: 6,
    background: '#fff',
  };

  const th = {
    fontSize: '0.62em',
    fontWeight: 800,
    color: GOLD_DARK,
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
    padding: '4px 3px',
    borderBottom: `1.5px solid ${GOLD}`,
    textAlign: 'right',
    whiteSpace: 'nowrap',
  };

  const td = {
    fontSize: '0.78em',
    padding: '4px 3px',
    borderBottom: '1px solid #eee',
    textAlign: 'right',
    verticalAlign: 'top',
  };

  const PhotoBox = ({ src, label, icon }) => (
    <div style={{ flex: 1, textAlign: 'center' }}>
      <div
        style={{
          height: 72,
          border: `1.5px dashed ${GOLD}`,
          borderRadius: 8,
          background: GOLD_SOFT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {src ? (
          <img src={src} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          icon
        )}
      </div>
      <div style={{ fontSize: '0.68em', fontWeight: 700, color: '#666', marginTop: 3 }}>{label}</div>
    </div>
  );

  return (
    <div
      className="pledge-print-a5-no-tear"
      style={{
        width: '100%',
        height: '100%',
        minHeight: '100%',
        padding: '3mm 3.5mm',
        boxSizing: 'border-box',
        fontFamily: 'Georgia, "Times New Roman", Times, serif',
        color: INK,
        fontSize: '11px',
        lineHeight: 1.25,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 6 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: '1.55em',
              fontWeight: 800,
              letterSpacing: '0.04em',
              color: INK,
              lineHeight: 1.1,
            }}
          >
            {companyData?.companyName || 'COMPANY NAME'}
          </div>
          <div style={{ fontSize: '0.78em', color: '#555', marginTop: 3 }}>
            {companyData?.address || 'Company Address'}
          </div>
        </div>
        <div style={{ flexShrink: 0, textAlign: 'right', fontSize: '0.72em', color: '#444' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 5, marginBottom: 3 }}>
            <IdIcon />
            <span>License No.: {companyData?.licenseNumber || '—'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 5 }}>
            <DocIcon />
            <span>FORM &apos;F&apos; — SEE RULE 12</span>
          </div>
        </div>
      </div>

      <div
        style={{
          alignSelf: 'center',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          border: `1.5px solid ${GOLD}`,
          borderRadius: 8,
          padding: '4px 16px',
          color: GOLD_DARK,
          fontWeight: 800,
          letterSpacing: '0.16em',
          fontSize: '1.05em',
          marginBottom: 7,
        }}
      >
        <TicketIcon />
        PAWN TICKET
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 7 }}>
        <div style={{ ...box, flex: 1, marginBottom: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <TicketIcon size={16} />
          <div>
            <div style={{ fontSize: '0.68em', color: '#777', fontWeight: 700, letterSpacing: '0.04em' }}>
              PAWN TICKET NO.
            </div>
            <div style={{ fontSize: '1.25em', fontWeight: 800 }}>
              <span style={{ background: GOLD_SOFT, color: GOLD_DARK, padding: '1px 6px', borderRadius: 3 }}>{ticketNo}</span>
            </div>
          </div>
        </div>
        <div style={{ ...box, flex: 1, marginBottom: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <CalIcon />
          <div>
            <div style={{ fontSize: '0.68em', color: '#777', fontWeight: 700, letterSpacing: '0.04em' }}>DATE</div>
            <div style={{ fontSize: '1.15em', fontWeight: 800 }}>
              <span style={{ background: GOLD_SOFT, color: GOLD_DARK, padding: '1px 6px', borderRadius: 3 }}>
                {formatDate(pledgeData.date)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div style={box}>
        <SectionTitle icon={<UserIcon />}>1. Pawner Details</SectionTitle>
        <FieldRow label="Name of Pawner:" value={pawnerName} highlight />
        <FieldRow label="Address:" value={address} />
        <FieldRow label="Mobile No:" value={c.mobileNo || '—'} />
        <FieldRow label="ID Proof (Type & No.):" value={idProof} />
      </div>

      <div style={box}>
        <SectionTitle icon={<RupeeIcon />}>2. Loan Details</SectionTitle>
        <FieldRow label="Principal Amount (₹):" value={`₹ ${formatAmount(principal)}`} highlight goldValue />
        <FieldRow label="Amount in Words:" value={`: ${numberToWords(principal)}`} />
        <FieldRow
          label="Redemption Period:"
          value={`${duration} Month${Number(duration) === 1 ? '' : 's'}`}
        />
        <FieldRow label="Rate of Interest:" value={`${roi}% per annum`} />
      </div>

      <div style={{ ...box, flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <SectionTitle icon={<GemIcon />}>3. Articles Pledged</SectionTitle>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left' }}>No.</th>
              <th style={{ ...th, textAlign: 'left' }}>Full Description of Article</th>
              <th style={th}>Qty.</th>
              <th style={th}>Gross Wt. (g)</th>
              <th style={th}>Deduction (g)</th>
              <th style={th}>Net Wt. (g)</th>
              <th style={th}>Purity (%)</th>
              <th style={th}>Assessed Value (₹)</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ ...td, textAlign: 'center', color: '#999' }}>
                  No articles
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.no}>
                  <td style={{ ...td, textAlign: 'left' }}>{r.no}</td>
                  <td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>{r.desc}</td>
                  <td style={td}>{r.qty}</td>
                  <td style={td}>{r.gross.toFixed(3)}</td>
                  <td style={td}>{r.deduction.toFixed(3)}</td>
                  <td style={td}>{r.net.toFixed(3)}</td>
                  <td style={td}>{r.purity}</td>
                  <td style={td}>{formatAmount(r.value)}</td>
                </tr>
              ))
            )}
            <tr>
              <td colSpan={2} style={{ ...td, textAlign: 'left', fontWeight: 800, borderBottom: 'none', background: GOLD_SOFT }}>
                TOTAL
              </td>
              <td style={{ ...td, fontWeight: 800, borderBottom: 'none', background: GOLD_SOFT }}>{totals.qty || ''}</td>
              <td style={{ ...td, fontWeight: 800, borderBottom: 'none', background: GOLD_SOFT }}>
                {totals.gross.toFixed(3)}
              </td>
              <td style={{ ...td, fontWeight: 800, borderBottom: 'none', background: GOLD_SOFT }}>
                {totals.deduction.toFixed(3)}
              </td>
              <td style={{ ...td, fontWeight: 800, borderBottom: 'none', background: GOLD_SOFT }}>
                {totals.net.toFixed(3)}
              </td>
              <td style={{ ...td, borderBottom: 'none', background: GOLD_SOFT }} />
              <td style={{ ...td, fontWeight: 800, borderBottom: 'none', background: GOLD_SOFT }}>
                {formatAmount(totals.value)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', gap: 10, margin: '6px 0' }}>
        <PhotoBox src={clientPhotoSrc} label="Pawner Photograph" icon={<UserIcon />} />
        <PhotoBox src={articlePhotoSrc} label="Pledged Article Photograph" icon={<CamIcon />} />
      </div>

      <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 2 }}>
            <PenIcon />
          </div>
          <div style={{ borderBottom: `1px solid ${INK}`, height: 28 }} />
          <div style={{ fontSize: '0.68em', fontWeight: 700, color: '#555', marginTop: 4 }}>
            Signature of Pawnbroker / Authorized Agent
          </div>
        </div>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', height: 28, marginBottom: 2 }}>
            {thumbImageSrc ? (
              <img
                src={thumbImageSrc}
                alt="Thumb"
                style={{ maxHeight: 28, maxWidth: 56, objectFit: 'contain' }}
              />
            ) : (
              <FingerIcon />
            )}
          </div>
          <div style={{ borderBottom: `1px solid ${INK}` }} />
          <div style={{ fontSize: '0.68em', fontWeight: 700, color: '#555', marginTop: 4 }}>
            Signature / Left Thumb Impression of Pawner
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          marginTop: 8,
          fontSize: '0.7em',
          color: '#666',
        }}
      >
        <ShieldIcon />
        Please retain this pawn ticket and produce it while redeeming the pledged articles.
      </div>
    </div>
  );
};

export default PledgePrintA5NoTear;
